<template>
  <div
    class="offcanvas offcanvas-end"
    :class="{ show: openCart }"
    tabindex="-1"
    id="offcanvas"
    data-testid="oc-product-cart-drawer"
    aria-labelledby="offcanvasLabel">
    <div class="offcanvas-header">
      <h5 class="offcanvas-title" id="offcanvasLabel">Products Summary</h5>
      <button type="button" class="btn-close text-reset" @click="openCart = false" aria-label="Close"></button>
    </div>
    <div class="product-wrapper" v-for="product in productsToSave" :key="product.id">
      <div class="product-item">
        <div class="avatar me-2" :style="{ backgroundColor: getProductBackground(product.id) }">
          <img :src="product.image" :alt="product.name" v-if="product.image" />
        </div>

        <div class="d-flex flex-column">
          <span class="fw-semibold">{{ product.name }}</span>
          <span class="text-muted product-description">{{ itemCartDescription(product) }}</span>
        </div>
        <div class="remove-icon" @click="removeProductToSave(product.id)">
          <IconX :size="16" />
        </div>
      </div>
    </div>
    <div class="mt-auto m-3 text-center" v-if="productsToSave.length > 0 && props.isProductsStep">
      <div class="cart-total">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fw-semibold">Total:</span>
          <span class="fw-bold fs-5">{{ formattedTotal }}</span>
        </div>
      </div>
      <LoadingButton
        class="btn btn-dark btn-lg w-100"
        data-testid="oc-product-cart-confirm-button"
        @press="validateProducts">
        <IconShoppingCart class="me-2" :size="20" />
        {{ $t('btn.confirm') }}
      </LoadingButton>
    </div>

    <div class="no-products-in-cart" v-if="productsToSave.length === 0">
      <h3 class="text-muted text-center">No products selected</h3>
    </div>
  </div>
  <div class="modal-backdrop show" v-if="openCart" @click="openCart = false"></div>
</template>
<script setup lang="ts">
import { validateSelectedProducts } from '@/api/product';
import { formatCurrency } from '@/locale/format';
import { useOrderCreationStore } from '@/stores/orderCreation';
import type { IProductToSave } from '@/types/product';
import { LoadingButton } from '@prospective/lithium';
import { IconShoppingCart, IconX } from '@tabler/icons-vue';
import { storeToRefs } from 'pinia';
import { useActiveBudgetStore } from '@/stores/activeBudget';

import { calculateCartTotal, getProductCartDescription, sumCartTotalInCurrency } from './cartCalculations';
import { useUserStore } from '@/stores/user';
import { convertCurrency } from '@/api/product';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

const openCart = defineModel<boolean>({ default: false });
const userStore = useUserStore();

const props = defineProps<{ isProductsStep: boolean }>();

const orderCreationStore = useOrderCreationStore();
const activeBudgetStore = useActiveBudgetStore();
const { selectedProducts, productsToSave } = storeToRefs(orderCreationStore);

const formattedTotal = ref<string>('');

// Calculate total whenever products change
watch(
  [productsToSave, selectedProducts],
  async () => {
    const totals = calculateCartTotal(productsToSave.value, selectedProducts.value);
    const targetCurrency = userStore.currentCustomer?.currency || 'CHF';

    const totalWithCurrency = await sumCartTotalInCurrency(totals, targetCurrency, convertCurrency);

    formattedTotal.value = formatCurrency(totalWithCurrency, targetCurrency);
  },
  { immediate: true, deep: true }
);

const emit = defineEmits<{
  (e: 'onProductsValidated'): void;
}>();

/* --------------------------------- METHODS -------------------------------- */
async function validateProducts(stopLoading: () => void) {
  stopLoading();
  try {
    await validateSelectedProducts(orderCreationStore.order.id, productsToSave.value);
    emit('onProductsValidated');
  } catch (error) {
    console.error('Error validating products:', error);
  } finally {
    stopLoading();
  }
}

function removeProductToSave(productId: number) {
  const index = productsToSave.value.findIndex((p) => p.id === productId);
  if (index > -1) {
    const productToRemove = productsToSave.value[index];
    const vBudgetId = productToRemove?.vBudget?.id;
    productsToSave.value.splice(index, 1);
    activeBudgetStore.deleteReservedVBudget(vBudgetId, productToRemove);
  }
}

function itemCartDescription(productToSave: IProductToSave) {
  return getProductCartDescription(productToSave, selectedProducts.value);
}

function getProductBackground(productId: number) {
  const product = selectedProducts.value.find((p) => p.id === productId);
  return product?.image_background || '#F3F3F3';
}
function handleEnterKey(event: KeyboardEvent) {
  if (event.key !== 'Enter') return;
  if (!openCart.value || !props.isProductsStep) return;
  if (productsToSave.value.length === 0) return;
  if (event.defaultPrevented) return;
  event.preventDefault();
  validateProducts(() => {});
}

onMounted(() => window.addEventListener('keydown', handleEnterKey));
onBeforeUnmount(() => window.removeEventListener('keydown', handleEnterKey));
</script>
<style scoped>
.modal-backdrop {
  z-index: 1040;
  opacity: 0.5;
}

.product-wrapper {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.product-wrapper:hover {
  background-color: rgba(0, 0, 0, 0.01);
}

.product-item {
  display: flex;
  position: relative;
}

.remove-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  color: #6c757d;
  border-radius: 50%;
  padding: 2px;
}

.remove-icon:hover {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.product-wrapper:hover .remove-icon {
  opacity: 1;
}

.avatar {
  flex: 0 0 70px;
  width: 70px;
  height: 70px;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.no-products-in-cart {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.cart-total {
  border-top: 2px dashed #e9ecef;
  padding-top: 1rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: bold;
}

.product-description {
  white-space: pre-line;
  line-height: 1.4;
}
</style>
